export const PIZZA_HALF_AND_HALF_MODE = "pizza_half_and_half";
export const PIZZA_HALF_AND_HALF_RULE_KEY = "pizza-half-and-half";
export const PIZZA_HALF_AND_HALF_PRICE_STRATEGY = "highest_price";
export const PIZZA_PRESENTATION_SELECTION_KEY = "pizza-presentation";
export const DEFAULT_PIZZA_SIZE_LABELS = [
  "Individual",
  "Chica",
  "Mediana",
  "Grande",
  "Familiar",
];

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const normalizeEntityId = (value) => String(value || "").trim();

const toSafeMoney = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, Number(parsed.toFixed(2))) : fallback;
};

const toOptionalSafeMoney = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return toSafeMoney(value, 0);
};

const buildPizzaSizeOptionId = (label, index = 0) => {
  const normalized = normalizeText(label)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || `pizza-size-${index + 1}`;
};

const getRawPizzaSizeOptions = (product) => {
  const options =
    product?.pizzaSizeOptions ||
    product?.pizza_size_options ||
    product?.pizzaSizes ||
    product?.pizza_sizes ||
    [];

  return Array.isArray(options) ? options : [];
};

const getProductCategoryId = (product) =>
  normalizeEntityId(product?.categoryId || product?.category_id || product?.categoria);

const getProductSubcategoryId = (product) =>
  normalizeEntityId(
    product?.subcategoryId ||
      product?.subcategory_id ||
      product?.subcategoriaId ||
      product?.subcategoria_id
  );

export const getProductSubcategoryName = (product, subcategories = []) => {
  const directName =
    product?.subcategoryName ||
    product?.subcategoriaNombre ||
    product?.subcategory_name ||
    product?.subcategoria ||
    "";

  if (directName) {
    return String(directName).trim();
  }

  const subcategoryId = getProductSubcategoryId(product);
  const match = (Array.isArray(subcategories) ? subcategories : []).find(
    (subcategory) => normalizeEntityId(subcategory?.id) === subcategoryId
  );

  return String(match?.name || match?.nombre || "").trim();
};

export const getPizzaProductName = (product, fallback = "Pizza") =>
  String(product?.name || product?.nombre || product?.productName || fallback)
    .trim()
    .replace(/\s+/g, " ") || fallback;

export const isReservedPizzaCategoryName = (value) => {
  const normalized = normalizeText(value);
  const words = normalized.split(/[^a-z0-9]+/g).filter(Boolean);

  return words.some((word) => word === "pizza" || word === "pizzas");
};

export const getProductCategoryName = (product, categories = []) => {
  const directName =
    product?.categoryName ||
    product?.categoriaNombre ||
    product?.category_name ||
    product?.category ||
    "";

  if (directName) {
    return String(directName).trim();
  }

  const categoryId = getProductCategoryId(product);
  const match = (Array.isArray(categories) ? categories : []).find(
    (category) => normalizeEntityId(category?.id) === categoryId
  );

  return String(match?.name || match?.nombre || "").trim();
};

export const isPizzaCategoryProduct = (
  product,
  categories = [],
  subcategories = []
) =>
  isReservedPizzaCategoryName(getProductCategoryName(product, categories)) ||
  isReservedPizzaCategoryName(getProductSubcategoryName(product, subcategories));

export const normalizePizzaSizeOption = (option = {}, index = 0) => {
  const label =
    String(
      option.label ||
        option.name ||
        option.nombre ||
        option.sizeLabel ||
        option.size_label ||
        ""
    )
      .trim()
      .replace(/\s+/g, " ") || `Tamano ${index + 1}`;
  const fullPrice = toSafeMoney(
    option.fullPrice ??
      option.full_price ??
      option.price ??
      option.precio ??
      option.amount
  );
  const explicitHalfPrice = toOptionalSafeMoney(
    option.halfPrice ??
      option.half_price ??
      option.mediaPrice ??
      option.media_price ??
      option.halfPizzaPrice ??
      option.half_pizza_price
  );
  const slices = Math.max(0, Math.floor(Number(option.slices ?? option.porciones ?? 0)));
  const explicitSlicePrice = toOptionalSafeMoney(
    option.slicePrice ??
      option.slice_price ??
      option.portionPrice ??
      option.portion_price ??
      option.precioPorcion ??
      option.precio_porcion
  );
  const recipeScale = Math.max(
    0,
    Number(option.recipeScale ?? option.recipe_scale ?? option.stockScale ?? option.stock_scale ?? 1) ||
      1
  );

  return {
    id: normalizeEntityId(option.id || option.optionId || option.sizeId) || buildPizzaSizeOptionId(label, index),
    label,
    fullPrice,
    halfPrice: explicitHalfPrice === null ? toSafeMoney(fullPrice / 2) : explicitHalfPrice,
    slicePrice: explicitSlicePrice === null ? 0 : explicitSlicePrice,
    slices,
    recipeScale,
    active: option.active !== false && option.enabled !== false,
    sellWhole: option.sellWhole !== false,
    sellHalf: option.sellHalf === true,
    allowHalfAndHalf: option.allowHalfAndHalf === true,
    sellSlice: option.sellSlice === true,
  };
};

export const normalizePizzaSizeOptions = (
  options = [],
  { includeInactive = false } = {}
) => {
  const seen = new Set();

  return (Array.isArray(options) ? options : [])
    .map(normalizePizzaSizeOption)
    .filter((option) => includeInactive || option.active !== false)
    .filter((option) => {
      const key = normalizeEntityId(option.id) || buildPizzaSizeOptionId(option.label);

      if (!key || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

export const getPizzaSizeOptions = (product) =>
  normalizePizzaSizeOptions(getRawPizzaSizeOptions(product));

export const isPizzaSaleModeEnabled = (option, mode = "full") => {
  if (!option) return mode === "full";
  if (option.active === false || option.enabled === false) return false;
  if (mode === "full") return option.sellWhole !== false && Number(option.fullPrice) > 0;
  if (mode === "half") return option.sellHalf === true && Number(option.halfPrice) > 0;
  if (mode === "half_and_half") return option.allowHalfAndHalf === true && Number(option.halfPrice) > 0;
  if (mode === "slice") return option.sellSlice === true && Number(option.slicePrice) > 0 && Number(option.slices) > 0;
  return false;
};

export const getPizzaSaleModes = (option) => ["full", "half", "half_and_half", "slice"]
  .filter((mode) => isPizzaSaleModeEnabled(option, mode));
export const getDefaultPizzaSaleMode = (option) => getPizzaSaleModes(option)[0] || "";
export const getPizzaSaleModePrice = (option, mode) => Number(
  mode === "full" ? option?.fullPrice : mode === "slice" ? option?.slicePrice : option?.halfPrice
) || 0;
export const getPizzaStartingPrice = (option) => {
  const prices = getPizzaSaleModes(option).map((mode) => getPizzaSaleModePrice(option, mode));
  return prices.length ? Math.min(...prices) : 0;
};
export const getPizzaSizePriceSummary = (option, format) => getPizzaSaleModes(option)
  .map((mode) => `${({ full: "Entera", half: "Media", half_and_half: "Mitad para combinar", slice: "Porcion" })[mode]} ${format(getPizzaSaleModePrice(option, mode))}`)
  .join(" - ");
export const getPizzaPortionScale = (option, mode) => mode === "half" || mode === "half_and_half"
  ? 0.5 : mode === "slice" ? 1 / Math.max(1, Number(option?.slices || 1)) : 1;

export const validatePizzaCartItems = (items = [], products = []) => {
  const byId = new Map(products.map((product) => [String(product.id || product.productId), product]));
  for (const item of items) {
    const product = byId.get(String(item.productId || item.sourceProductId || item.product_id || ""));
    const options = product ? getPizzaSizeOptions(product) : [];
    const half = getPizzaHalfAndHalfSelection(item.linkedSelections || []) ||
      (isPizzaHalfAndHalfSelection(item) ? item : null);
    const sizeId = item.pizzaSizeOptionId || half?.pizzaSizeOptionId || "";
    const mode = half ? "half_and_half" : item.pizzaPortionMode || "full";
    if (!getRawPizzaSizeOptions(product).length && !sizeId && !half && mode === "full") continue;
    const option = options.find((entry) => entry.id === sizeId);
    const fail = () => ({ ok: false, message: `La modalidad o el tamano de ${getPizzaProductName(product, item.nombre || item.name || "esta pizza")} ya no esta disponible. Edita o quita el producto del pedido.` });
    if (!product || product.active === false || !option || !isPizzaSaleModeEnabled(option, mode) ||
        (mode === "half_and_half" && !half)) return fail();
    if (half) {
      const other = byId.get(String(half.productId || half.linkedHalfProductId));
      const match = other && findMatchingPizzaSizeOption(other, option);
      if (!other || other.active === false || !match || !isPizzaSaleModeEnabled(match, "half_and_half") ||
          String(other.id) === String(product.id) || getProductCategoryId(other) !== getProductCategoryId(product)) return fail();
    }
  }
  return { ok: true, message: "" };
};

export const productHasPizzaSizeOptions = (product) =>
  getPizzaSizeOptions(product).length > 0;

export const findPizzaSizeOption = (product, sizeOptionId = "") => {
  const options = getPizzaSizeOptions(product);
  const safeSizeOptionId = normalizeEntityId(sizeOptionId);

  if (!options.length) {
    return null;
  }

  if (!safeSizeOptionId) {
    return options[0];
  }

  const normalizedSizeKey = normalizeText(safeSizeOptionId);

  return (
    options.find(
      (option) =>
        option.id === safeSizeOptionId ||
        normalizeText(option.label) === normalizedSizeKey
    ) || options[0]
  );
};

export const findMatchingPizzaSizeOption = (product, referenceOption = null) => {
  if (!referenceOption) {
    return findPizzaSizeOption(product);
  }

  const options = getPizzaSizeOptions(product);
  const referenceId = normalizeEntityId(referenceOption.id);
  const referenceLabel = normalizeText(referenceOption.label);

  return (
    options.find(
      (option) =>
        (referenceId && option.id === referenceId) ||
        (referenceLabel && normalizeText(option.label) === referenceLabel)
    ) || null
  );
};

export const resolvePizzaProductPrice = (
  product,
  { sizeOptionId = "", sizeOption = null, portionMode = "full" } = {}
) => {
  if (!product) {
    return 0;
  }

  const resolvedSizeOption = sizeOption || findPizzaSizeOption(product, sizeOptionId);

  if (resolvedSizeOption) {
    if (portionMode === "half") {
      return toSafeMoney(resolvedSizeOption.halfPrice);
    }

    if (portionMode === "slice") {
      return toSafeMoney(resolvedSizeOption.slicePrice);
    }

    return toSafeMoney(resolvedSizeOption.fullPrice);
  }

  if (product.price !== undefined && product.price !== null && product.price !== "") {
    return Number(product.price || 0);
  }

  if (product.precio !== undefined && product.precio !== null && product.precio !== "") {
    return Number(product.precio || 0);
  }

  return Number(product.suggestedPrice || product.precioSugerido || 0);
};

export const buildPizzaHalfAndHalfOptions = (
  product,
  products = [],
  categories = [],
  { sizeOptionId = "", subcategories = [] } = {}
) => {
  if (!product || !isPizzaCategoryProduct(product, categories, subcategories)) {
    return [];
  }

  const productId = normalizeEntityId(product?.id || product?.productId);
  const categoryId = getProductCategoryId(product);
  const subcategoryId = getProductSubcategoryId(product);
  const baseHasSizes = productHasPizzaSizeOptions(product);
  const selectedSizeOption = baseHasSizes
    ? findPizzaSizeOption(product, sizeOptionId)
    : null;

  if (!isPizzaSaleModeEnabled(selectedSizeOption, "half_and_half")) return [];

  return (Array.isArray(products) ? products : [])
    .filter((candidate) => {
      const candidateId = normalizeEntityId(candidate?.id || candidate?.productId);

      if (!candidateId || candidateId === productId || candidate?.active === false) {
        return false;
      }

      if (!isPizzaCategoryProduct(candidate, categories, subcategories)) {
        return false;
      }

      if (categoryId && getProductCategoryId(candidate) !== categoryId) {
        return false;
      }

      if (baseHasSizes) {
        return isPizzaSaleModeEnabled(findMatchingPizzaSizeOption(candidate, selectedSizeOption), "half_and_half");
      }

      const candidateSubcategoryId = getProductSubcategoryId(candidate);
      return !subcategoryId || !candidateSubcategoryId || candidateSubcategoryId === subcategoryId;
    })
    .sort((left, right) =>
      getPizzaProductName(left).localeCompare(getPizzaProductName(right), "es")
    );
};

export const getPizzaHalfAndHalfExtraUnitPrice = (
  baseProduct,
  halfProduct,
  options = {}
) => getPizzaHalfAndHalfPricing(baseProduct, halfProduct, options).extraUnitPrice;

export const getPizzaHalfAndHalfPricing = (
  baseProduct,
  halfProduct,
  { sizeOptionId = "" } = {}
) => {
  const baseSizeOption = findPizzaSizeOption(baseProduct, sizeOptionId);
  const linkedSizeOption = findMatchingPizzaSizeOption(halfProduct, baseSizeOption);
  const hasSizedPricing = Boolean(baseSizeOption || linkedSizeOption);
  const baseFullUnitPrice = resolvePizzaProductPrice(baseProduct, {
    sizeOption: baseSizeOption,
  });

  if (hasSizedPricing) {
    const baseHalfUnitPrice = resolvePizzaProductPrice(baseProduct, {
      sizeOption: baseSizeOption,
      portionMode: "half",
    });
    const linkedHalfUnitPrice = resolvePizzaProductPrice(halfProduct, {
      sizeOption: linkedSizeOption,
      portionMode: "half",
    });
    const totalUnitPrice = toSafeMoney(baseHalfUnitPrice + linkedHalfUnitPrice);

    return {
      sizeOption: baseSizeOption,
      linkedSizeOption,
      baseFullUnitPrice,
      baseHalfUnitPrice,
      linkedHalfUnitPrice,
      totalUnitPrice,
      extraUnitPrice: Number((totalUnitPrice - baseFullUnitPrice).toFixed(2)),
    };
  }

  const linkedFullUnitPrice = resolvePizzaProductPrice(halfProduct);
  const totalUnitPrice = Math.max(baseFullUnitPrice, linkedFullUnitPrice);

  return {
    sizeOption: null,
    linkedSizeOption: null,
    baseFullUnitPrice,
    baseHalfUnitPrice: toSafeMoney(baseFullUnitPrice / 2),
    linkedHalfUnitPrice: toSafeMoney(linkedFullUnitPrice / 2),
    totalUnitPrice,
    extraUnitPrice: Math.max(0, linkedFullUnitPrice - baseFullUnitPrice),
  };
};

export const createPizzaHalfAndHalfSelection = (
  baseProduct,
  halfProduct,
  { sizeOptionId = "" } = {}
) => {
  if (!baseProduct || !halfProduct) {
    return null;
  }

  const baseSize = findPizzaSizeOption(baseProduct, sizeOptionId);
  const otherSize = findMatchingPizzaSizeOption(halfProduct, baseSize);
  if (!isPizzaSaleModeEnabled(baseSize, "half_and_half") ||
      !isPizzaSaleModeEnabled(otherSize, "half_and_half")) return null;

  const pricing = getPizzaHalfAndHalfPricing(baseProduct, halfProduct, {
    sizeOptionId,
  });

  return {
    ruleKey: PIZZA_HALF_AND_HALF_RULE_KEY,
    ruleTitle: "Mitad y mitad",
    groupLabel: "Mitad y mitad",
    productId: normalizeEntityId(halfProduct?.id || halfProduct?.productId),
    productName: getPizzaProductName(halfProduct),
    unitPrice: pricing.extraUnitPrice,
    baseUnitPrice: pricing.baseFullUnitPrice,
    baseHalfUnitPrice: pricing.baseHalfUnitPrice,
    linkedHalfUnitPrice: pricing.linkedHalfUnitPrice,
    totalUnitPrice: pricing.totalUnitPrice,
    splitMode: PIZZA_HALF_AND_HALF_MODE,
    priceStrategy: pricing.sizeOption ? "sum_half_prices" : PIZZA_HALF_AND_HALF_PRICE_STRATEGY,
    pizzaSizeOptionId: pricing.sizeOption?.id || "",
    pizzaSizeLabel: pricing.sizeOption?.label || "",
    baseHalfProductId: normalizeEntityId(baseProduct?.id || baseProduct?.productId),
    baseHalfProductName: getPizzaProductName(baseProduct),
    linkedHalfProductId: normalizeEntityId(halfProduct?.id || halfProduct?.productId),
    linkedHalfProductName: getPizzaProductName(halfProduct),
  };
};

export const isPizzaHalfAndHalfSelection = (selection) =>
  String(selection?.splitMode || "") === PIZZA_HALF_AND_HALF_MODE ||
  String(selection?.ruleKey || "") === PIZZA_HALF_AND_HALF_RULE_KEY;

export const getPizzaHalfAndHalfSelection = (linkedSelections = []) =>
  (Array.isArray(linkedSelections) ? linkedSelections : []).find(
    isPizzaHalfAndHalfSelection
  ) || null;

export const buildPizzaHalfAndHalfSelectionDetails = (baseProduct, selection) => {
  if (!isPizzaHalfAndHalfSelection(selection)) {
    return [];
  }

  const details = [
    `1/2 ${selection.baseHalfProductName || getPizzaProductName(baseProduct)}`,
    `1/2 ${selection.linkedHalfProductName || selection.productName || "Pizza"}`,
  ];

  return selection.pizzaSizeLabel ? [`Tamano: ${selection.pizzaSizeLabel}`, ...details] : details;
};

export const createPizzaSizeSelectionDetail = (sizeOption) =>
  sizeOption?.label ? `Tamano: ${sizeOption.label}` : "";
